import i18n from '@/i18n'
import { formatCoordinate } from '@/utils/math'
import { CoreEntityDto } from '@/interfaces/core.interface'
import { useGeneralStore } from '@/pinia'

export type placeSummaryItem = {
  title: string
  value1?: string
  value2?: string
  info?: string
  isHeader?: boolean
  unit?: string
  hasDivider?: boolean
}

type poiMetaInfoItem = {
  title: string
  type: string
  value: string
}

export function generateAddress(currentEntity: CoreEntityDto): placeSummaryItem[] {
  const { t } = i18n.global
  const address = currentEntity?.properties?.address
  const place = currentEntity?.properties?.place
  const zipCode = currentEntity?.properties?.zipCode
  if (address?.length || place?.length || zipCode?.length) {
    return [
      {
        title: t('general__title__address'),
        value1: address,
        value2: zipCode?.length ? zipCode + ' ' : '' + place?.length ? place : ''
      }
    ]
  }
  return []
}

export function generateAddressList(currentEntity: CoreEntityDto) {
  const { t } = i18n.global
  const addressArray: placeSummaryItem[] = []
  if (currentEntity?.properties?.addresses?.length) {
    currentEntity?.properties?.addresses.forEach((address) => {
      const zipCode = address.zipCode?.length > 0 ? address.zipCode : ''
      const place = address.place?.length > 0 ? address.place : ''
      let zipAddress = ''
      if (zipCode.length && place.length) {
        zipAddress = zipCode + ' ' + place
      } else if (zipCode.length) {
        zipAddress = zipCode
      } else if (place.length) {
        zipAddress = place
      }
      addressArray.push({
        title: t('general__title__address'),
        value1: address.streetAddress,
        value2: zipAddress
      })
    })
  }
  return addressArray
}

export function generatePlaceSummary(currentEntity: CoreEntityDto) {
  const { t } = i18n.global
  const placeArray: placeSummaryItem[] = []
  if (currentEntity?.properties?.place) {
    placeArray.push({
      title: t('general__text__location'),
      value1: currentEntity?.properties?.place
    })
  }
  if (currentEntity?.location?.coordinates?.length) {
    placeArray.push(
      {
        title: t('general__text__coordinates-wgs-84'),
        info: t('info__coordinates-wgs-84'),
        isHeader: true,
        hasDivider:
          currentEntity?.properties?.place?.length && currentEntity?.properties?.place?.length > 0
            ? true
            : false
      },
      {
        title: t('general__text__latitude'),
        value1: formatCoordinate(currentEntity.location.coordinates[1])
      },
      {
        title: t('general__text__longitude'),
        value1: formatCoordinate(currentEntity.location.coordinates[0])
      }
    )
  }
  return placeArray
}

export function generatePoiPlaceSummary(poiMetaInfo: any) {
  const { t } = i18n.global
  const placeArray: placeSummaryItem[] = []
  if (poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightRH' && info.value)) {
    placeArray.push({
      title: t('general__text__height-in-RH'),
      value1: poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightRH').value,
      info: t('info__poi-height-rh2000-infodisplay'),
      unit: 'm'
    })
  }
  if (poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightFrom' && info.value)) {
    placeArray.push({
      title: poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightFrom').title, // Can be height from ground or floor
      value1: poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightFrom').value,
      info: t('info__poi-estimated-height-infodisplay'),
      unit: 'm'
    })
  }
  if (
    poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'longitude' && info.value) ||
    poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'latitude' && info.value)
  ) {
    placeArray.push({
      title: t('general__text__coordinates-wgs-84'),
      info: t('info__coordinates-wgs-84'),
      isHeader: true,
      hasDivider:
        poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightRH' && info.value) ||
        poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'heightFrom' && info.value)
          ? true
          : false
    })
  }
  if (poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'latitude' && info.value)) {
    placeArray.push({
      title: t('general__text__latitude'),
      value1: poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'latitude').value
    })
  }
  if (poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'longitude' && info.value)) {
    placeArray.push({
      title: t('general__text__longitude'),
      value1: poiMetaInfo.find((info: poiMetaInfoItem) => info.type === 'longitude').value
    })
  }
  return placeArray
}

export function generateZoneSummary() {
  const piniaStore = useGeneralStore()
  const selectedZone = piniaStore.selectedZone
  const { t } = i18n.global
  const placeArray: placeSummaryItem[] = []
  if (selectedZone?.centroid)
    placeArray.push(
      {
        title: t('general__text__coordinates-wgs-84'),
        info: t('info__coordinates-wgs-84'),
        isHeader: true
      },
      {
        title: t('general__text__latitude'),
        value1: formatCoordinate(selectedZone.centroid[1])
      },
      {
        title: t('general__text__longitude'),
        value1: formatCoordinate(selectedZone.centroid[0])
      }
    )
  return placeArray
}
